import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';

export type RoundAction = 'validate' | 'refuse' | 'modify';

interface Props {
  /** Le geste qui attend sa confirmation — `null` tant que rien n'est demandé. */
  action: RoundAction | null;
  roundNumber: number | null;
  onCancel: () => void;
  onConfirm: () => void;
  busy?: boolean;
}

const CONFIRM_BUTTON_CLASS: Record<RoundAction, string> = {
  refuse: 'bg-hot-pink text-[var(--on-accent)]',
  validate: 'bg-mint',
  modify: 'bg-ink text-paper',
};

/**
 * SC-715 — la deuxième confirmation avant un refus ; SC-953 — la même devant
 * Valider et devant Modifier.
 *
 * Refuser était un clic unique, sur une tablette tenue à une main en plein
 * service, à côté du bouton Valider : la fausse manœuvre refusait la commande
 * d'un client. Et le geste ne se rattrape pas — un refus de ronde SUPPRIME ses
 * lignes (SC-356), le journal des refus n'en garde qu'une trace morte (SC-694).
 * Valider envoie en cuisine, Modifier retire la ronde de la file pour la
 * mettre dans le panier du serveur : aucun des trois ne se défait, aucun ne
 * part au premier appui.
 *
 * Toutes les surfaces qui traitent une ronde montent ce même dialogue — le
 * veilleur global (`WaiterPendingOrdersHost`) et la feuille d'une table
 * (`PendingRoundsSheet`) — pour que la garde ne puisse pas exister d'un côté
 * seulement. Les testids gardent le préfixe du geste (`refuse-confirm`,
 * `validate-confirm`, `modify-confirm`).
 */
export default function RoundActionConfirmDialog({
  action,
  roundNumber,
  onCancel,
  onConfirm,
  busy = false,
}: Props) {
  const { t } = useTranslation();
  const shown = action ?? 'refuse';
  const title = t(`waiter.pendingSheet.${shown}ConfirmTitle`, { n: roundNumber ?? '' });

  return (
    <Modal
      open={action !== null && roundNumber !== null}
      onClose={onCancel}
      // DF-0223 — un appui a cote annulait la confirmation en silence : le
      // serveur venait d'appuyer sur Valider, il croit la ronde partie, rien
      // n'est parti. Mesure Yuji 2026-09-11 : 297 s et 787 s entre la tournee
      // du client et son bon de cuisine. Seul « Annuler » annule.
      dismissOnBackdrop={false}
      ariaLabel={title}
    >
      <div data-testid={`${shown}-confirm`} className="p-5 flex flex-col gap-4">
        <h2 className="font-display text-lg">{title}</h2>
        <p className="font-mono text-sm text-ink/70">
          {t(`waiter.pendingSheet.${shown}ConfirmBody`)}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid={`${shown}-cancel`}
            onClick={onCancel}
            className="flex-1 bg-paper border-2 border-ink rounded-btn px-4 py-2 font-display"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            data-testid={`${shown}-confirm-button`}
            disabled={busy}
            onClick={onConfirm}
            className={`flex-1 border-2 border-ink rounded-btn px-4 py-2 font-display disabled:opacity-50 ${CONFIRM_BUTTON_CLASS[shown]}`}
          >
            {t(`waiter.pendingSheet.${shown}ConfirmButton`)}
          </button>
        </div>
      </div>
    </Modal>
  );
}
